/**
 * Theme provider — Bootstrap 5.3 native dark mode via data-bs-theme attribute.
 *
 * On first load: respects prefers-color-scheme.
 * After: persists user choice in localStorage; survives reloads and tenant
 * subdomain navigation (same-origin storage).
 *
 * The hook + context live in ./useTheme.ts so this file only exports the
 * component (react-refresh/only-export-components — required for HMR).
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { ThemeContext, type Mode, type ThemeValue } from "./useTheme";

const STORAGE_KEY = "naas_theme";

function initialMode(): Mode {
  const stored = localStorage.getItem(STORAGE_KEY) as Mode | null;
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<Mode>(initialMode);

  useEffect(() => {
    document.documentElement.setAttribute("data-bs-theme", mode);
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  const toggle = useCallback(() => setMode((m) => (m === "dark" ? "light" : "dark")), []);

  const value = useMemo<ThemeValue>(() => ({ mode, toggle, set: setMode }), [mode, toggle]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
